import { useEffect, useRef, useState } from "react";
import { Camera, Upload, X, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function CameraDialog({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: "user", width: { ideal: 1280 } }, audio: false })
      .then((s) => {
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => setError("Não foi possível acessar a câmera. Verifique a permissão ou envie uma foto."));
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, [open]);

  const capture = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => {
      onCapture(new File([b], "selfie.jpg", { type: "image/jpeg" }));
      onClose();
    }, "image/jpeg", 0.92);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="p-0 overflow-hidden max-w-md" data-testid="camera-dialog">
        <DialogTitle className="sr-only">Tirar selfie</DialogTitle>
        <div className="relative bg-black aspect-[3/4]">
          {error ? (
            <p className="absolute inset-0 grid place-items-center p-8 text-center text-white text-sm" data-testid="camera-error">{error}</p>
          ) : (
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" />
          )}
          <div className="absolute inset-0 pointer-events-none grid place-items-center">
            <div className="w-[62%] aspect-[3/4] rounded-[50%] border-2 border-white/80 border-dashed" />
          </div>
        </div>
        <div className="p-4 flex gap-3">
          <button onClick={onClose} data-testid="camera-cancel-btn" className="flex-1 h-12 rounded-md border border-zinc-200 font-medium">
            Cancelar
          </button>
          <button
            onClick={capture}
            disabled={!!error}
            data-testid="camera-capture-btn"
            className="flex-[2] h-12 rounded-md bg-[#0055FF] hover:bg-[#0040CC] disabled:opacity-40 text-white font-semibold flex items-center justify-center gap-2"
          >
            <Camera className="w-5 h-5" /> Capturar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SelfieBar({ onSelfie, selfie, searching, onReset }) {
  const fileRef = useRef(null);
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!selfie) return setSrc(null);
    const u = URL.createObjectURL(selfie);
    setSrc(u);
    return () => URL.revokeObjectURL(u);
  }, [selfie]);
  const [cam, setCam] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (f) onSelfie(f);
    e.target.value = "";
  };

  return (
    <>
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white/85 backdrop-blur-xl border-t border-black/5" data-testid="selfie-bar">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          {selfie && (
            <div className="relative shrink-0">
              <img src={src || ""} alt="Selfie" className="w-12 h-12 rounded-md object-cover border border-zinc-200" data-testid="selfie-preview" />
              <button onClick={onReset} data-testid="selfie-reset-btn" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-zinc-900 text-white grid place-items-center">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <button
            onClick={() => setCam(true)}
            disabled={searching}
            data-testid="open-camera-btn"
            className="flex-1 h-12 rounded-md bg-[#0055FF] hover:bg-[#0040CC] disabled:opacity-60 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            {searching ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            {searching ? "Procurando…" : "Tirar selfie"}
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={searching}
            data-testid="upload-selfie-btn"
            className="flex-1 h-12 rounded-md border border-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-60 font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <Upload className="w-5 h-5" /> Enviar foto
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="selfie-file-input" />
        </div>
      </div>
      <CameraDialog open={cam} onClose={() => setCam(false)} onCapture={onSelfie} />
    </>
  );
}
